/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */

import { Heap, type IComparer } from "@fluidframework/core-utils/internal";
import type { ISequencedDocumentMessage } from "@fluidframework/driver-definitions/internal";

import { MergeTreeTextHelper } from "../MergeTreeTextHelper.js";
import { RedBlackTree } from "../collections/index.js";
import { compareNumbers } from "../mergeTreeNodes.js";
import { PriorPerspective } from "../perspective.js";
import type { PropertySet } from "../properties.js";

import { TestClient } from "./testClient.js";

interface ClientSeq {
	refSeq: number;
	clientId: string;
}

const clientSeqComparer: IComparer<ClientSeq> = {
	min: { refSeq: -1, clientId: "" },
	compare: (a, b) => a.refSeq - b.refSeq,
};

/**
 * Server for tests.  Simulates client communication by directing placing
 * messages in client queues.
 */
export class TestServer extends TestClient {
	seq = 1;
	clients: TestClient[] = [];
	clientSeqNumbers: Heap<ClientSeq> = new Heap<ClientSeq>(clientSeqComparer);
	upstreamMap: RedBlackTree<number, number> = new RedBlackTree<number, number>(compareNumbers);
	constructor(options?: PropertySet) {
		super(options);
	}

	addClients(clients: TestClient[]): void {
		this.clientSeqNumbers = new Heap<ClientSeq>(clientSeqComparer);
		this.clients = clients;
		for (const client of clients) {
			this.clientSeqNumbers.add({
				refSeq: client.getCurrentSeq(),
				clientId: client.longClientId ?? "",
			});
		}
	}

	applyMsg(msg: ISequencedDocumentMessage): boolean {
		super.applyMsg(msg);
		if (TestClient.useCheckQ) {
			const clientId = this.getShortClientId(msg.clientId as string);
			const perspective = new PriorPerspective(msg.referenceSequenceNumber, clientId);
			return checkTextMatchRelative(perspective, this, msg);
		} else {
			return false;
		}
	}

	// TODO: remove mappings when no longer needed using min seq
	// in upstream message
	transformUpstreamMessage(msg: ISequencedDocumentMessage): void {
		if (msg.referenceSequenceNumber > 0) {
			msg.referenceSequenceNumber =
				this.upstreamMap.get(msg.referenceSequenceNumber)?.data ?? 0;
		}
		msg.origin = {
			id: "A",
			sequenceNumber: msg.sequenceNumber,
			minimumSequenceNumber: msg.minimumSequenceNumber,
		};
		this.upstreamMap.put(msg.sequenceNumber, this.seq);
		msg.sequenceNumber = -1;
	}

	copyMsg(msg: ISequencedDocumentMessage): ISequencedDocumentMessage {
		return {
			clientId: msg.clientId,
			clientSequenceNumber: msg.clientSequenceNumber,
			contents: msg.contents,
			minimumSequenceNumber: msg.minimumSequenceNumber,
			referenceSequenceNumber: msg.referenceSequenceNumber,
			sequenceNumber: msg.sequenceNumber,
			type: msg.type,
		} as unknown as ISequencedDocumentMessage;
	}

	private minSeq = 0;

	applyMessages(msgCount: number): boolean {
		let _msgCount = msgCount;
		while (_msgCount > 0) {
			const msg = this.dequeueMsg();
			if (msg) {
				if (msg.sequenceNumber >= 0) {
					this.transformUpstreamMessage(msg);
				}
				msg.sequenceNumber = this.seq++;
				msg.minimumSequenceNumber = this.minSeq;
				if (this.applyMsg(msg)) {
					return true;
				}
				if (this.clients) {
					let minCli = this.clientSeqNumbers.peek()?.value;
					if (
						// eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- TODO: ADO#58520 Code owners should verify if this code change is safe and make it if so or update this comment otherwise
						minCli &&
						minCli.clientId === msg.clientId &&
						minCli.refSeq < msg.referenceSequenceNumber
					) {
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						const cliSeq = this.clientSeqNumbers.get()!;
						const oldSeq = cliSeq.refSeq;
						cliSeq.refSeq = msg.referenceSequenceNumber;
						this.clientSeqNumbers.add(cliSeq);
						minCli = this.clientSeqNumbers.peek()?.value;
						if (minCli && minCli.refSeq > oldSeq) {
							msg.minimumSequenceNumber = minCli.refSeq;
							this.minSeq = minCli.refSeq;
						}
					}
					for (const client of this.clients) {
						client.enqueueMsg(msg);
					}
				}
			} else {
				break;
			}
			_msgCount--;
		}
		return false;
	}
}

/**
 * Used for in-memory testing.  This will queue a reference string for each client message.
 */
export function checkTextMatchRelative(
	perspective: PriorPerspective,
	server: TestServer,
	msg: ISequencedDocumentMessage,
): boolean {
	const client = server.clients[perspective.clientId];
	const serverText = new MergeTreeTextHelper(server.mergeTree).getText(perspective);
	const cliText = client.checkQ.shift()?.data;
	if (cliText === undefined || cliText !== serverText) {
		console.log(`mismatch `);
		console.log(msg);
		// eslint-disable-next-line @typescript-eslint/no-base-to-string
		console.log(server.mergeTree.toString());
		// eslint-disable-next-line @typescript-eslint/no-base-to-string
		console.log(client.mergeTree.toString());
		return true;
	}
	return false;
}
